import {Injectable} from "@angular/core";
import {Observable} from "rxjs";
import {HttpEvent} from "@angular/common/http";
import {CasaNativeApiV3} from "../../../casa-native-api";
import {V3FileItem} from "../../../../../model/v3/file";
import {
  CountryDefinition,
  V3Address,
  V3ContactEntry,
  V3ContactEntryDetails,
  V3Person,
  V3PersonalSensitiveDetails,
  V3PersonDetails,
  V3Settings,
} from "../../../../../model/v3/people";
import {
  SchoolStudentStatus,
  V3SchoolTeacher,
  V3School,
  V3Student,
  V3Teacher,
} from "../../../../../model/v3/schools";
import {Page, Pageable} from "../../../../../../utils/pageable";
import {FileV3RestService} from "../../../v3/file-rest.service";
import {PeopleV3RestService} from "../../../v3/people-rest.service";
import {PublicV3RestService} from "../../../v3/public-rest.service";
import {SchoolV3RestService} from "../../../v3/school-rest.service";
import {StudentV3RestService} from "../../../v3/student-rest.service";
import {TeacherV3RestService} from "../../../v3/teacher-rest.service";

@Injectable({
  providedIn: 'root'
})
export class WebCasaNativeV3Service implements CasaNativeApiV3 {
  isImplemented(): boolean {
    return true;
  }

  isSupportsProfileEdit(): Boolean {
    return true;
  }

  supportsFileSending(): boolean {
    return true;
  }

  constructor(
    public readonly files: FileV3RestService,
    public readonly people: PeopleV3RestService,
    public readonly publicApi: PublicV3RestService,
    public readonly schools: SchoolV3RestService,
    public readonly students: StudentV3RestService,
    public readonly teachers: TeacherV3RestService,
  ) {
  }

  uploadFile(file: File): Observable<HttpEvent<any>> {
    return this.files.uploadFile(file);
  }

  getPerson(personId: number): Observable<V3Person> {
    return this.people.getPerson(personId);
  }

  updatePerson(personId: number, details: V3PersonDetails): Observable<V3Person> {
    return this.people.updatePerson(personId, details);
  }

  deletePerson(personId: number): Observable<void> {
    return this.people.deletePerson(personId);
  }

  listContacts(personId: number): Observable<V3ContactEntry[]> {
    return this.people.listContacts(personId);
  }

  createContact(personId: number, contact: V3ContactEntryDetails): Observable<void> {
    return this.people.createContact(personId, contact);
  }

  updateContact(personId: number, contactId: number, contact: V3ContactEntryDetails): Observable<void> {
    return this.people.updateContact(personId, contactId, contact);
  }

  getAddress(personId: number): Observable<V3Address> {
    return this.people.getAddress(personId);
  }

  updateAddress(personId: number, address: V3Address): Observable<void> {
    return this.people.updateAddress(personId, address);
  }

  getSensitive(personId: number): Observable<V3PersonalSensitiveDetails> {
    return this.people.getSensitive(personId);
  }

  updateSensitive(personId: number, sensitive: V3PersonalSensitiveDetails): Observable<void> {
    return this.people.updateSensitive(personId, sensitive);
  }

  getPhoto(personId: number): Observable<V3FileItem> {
    return this.people.getPhoto(personId);
  }

  updatePhoto(personId: number, file: V3FileItem): Observable<void> {
    return this.people.updatePhoto(personId, file);
  }

  getSettings(personId: number): Observable<V3Settings> {
    return this.people.getSettings(personId);
  }

  updateSettings(personId: number, settings: V3Settings): Observable<void> {
    return this.people.updateSettings(personId, settings);
  }

  listCountries(): Observable<CountryDefinition[]> {
    return this.publicApi.listCountries();
  }

  getSchool(schoolId: number): Observable<V3School> {
    return this.schools.getSchool(schoolId);
  }

  getStudents(
    schoolId: number,
    term?: string | null,
    status?: SchoolStudentStatus[] | null,
    expiry?: Date | string | null,
    pageable?: Pageable,
  ): Observable<Page<V3Student>> {
    return this.students.getStudents(schoolId, term, status, expiry, pageable);
  }

  getStudent(schoolId: number, studentId: number): Observable<V3Student> {
    return this.students.getStudent(schoolId, studentId);
  }

  getTeacher(schoolId: number, teacherId: number): Observable<V3Teacher> {
    return this.teachers.getTeacher(schoolId, teacherId);
  }

  getTeachers(
    schoolId?: number | null,
    personId?: number | null,
    email?: string | null,
    teacherIds?: number[] | null,
    pageable?: Pageable | null
  ): Observable<Page<V3SchoolTeacher>> {
    return this.teachers.getTeachers(schoolId, personId, email, teacherIds, pageable);
  }

  findTeachers(
    schoolId: number,
    term?: string | null,
    productCode?: string | null,
    pageable?: Pageable | null
  ): Observable<Page<V3Teacher>> {
    return this.teachers.findTeachers(schoolId, term, productCode, pageable);
  }
}
